import { differenceInMinutes, differenceInSeconds, parseISO } from 'date-fns';
import { Market } from '../types/marketTypes';

export const MARKETS_POLL_MILLIS = 5000;
export const EVENT_POLL_MILLIS = 2000;

/** How long after the start an event without markets is still polled. */
export const NO_MARKETS_POLL_MINUTES = 10;

/** How long polling goes on once the page has seen the last market get its outcome. */
export const RESOLVED_POLL_SECONDS = 60;

/** No event is polled longer than this after its start. */
export const MAX_POLL_MINUTES = 24 * 60;

export const allResolved = (markets: Market[]): boolean =>
  markets.length > 0 && markets.every(market => !!market.marketOutcome);

/**
 * Whether the event page keeps polling: while any market has no outcome yet — settled and voided
 * markets have one, a market merely closed does not — and for {@link RESOLVED_POLL_SECONDS} after
 * `resolvedAt`, the moment the page saw the last one get it; capped at {@link MAX_POLL_MINUTES} after
 * the start. An event without markets is polled until {@link NO_MARKETS_POLL_MINUTES} after the start.
 */
export function isEventLive(startTime: string | undefined, markets: Market[], now: Date, resolvedAt?: Date): boolean {
  const sinceStart = startTime ? differenceInMinutes(now, parseISO(startTime)) : 0;
  if (sinceStart > MAX_POLL_MINUTES) {
    return false;
  }
  if (markets.length === 0) {
    return sinceStart <= NO_MARKETS_POLL_MINUTES;
  }
  if (!allResolved(markets)) {
    return true;
  }
  return !!resolvedAt && differenceInSeconds(now, resolvedAt) <= RESOLVED_POLL_SECONDS;
}
